import React from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Award,
  Users,
  Heart,
  Target,
  BookOpen,
  Trophy,
  CheckCircle,
  ArrowRight,
  Shield,
  type LucideIcon
} from 'lucide-react'
import Seo from '../components/Seo'

interface Certification {
  title: string
  organization: string
  icon: LucideIcon
}

const About: React.FC = () => {
  const qualifications: Certification[] = [
    {
      title: 'Certified Personal Trainer',
      organization: 'NASM',
      icon: Award
    },
    {
      title: 'Certified Nutrition Coach',
      organization: 'NASM',
      icon: BookOpen
    },
    {
      title: 'Physique & Bodybuilding Coach',
      organization: 'NASM',
      icon: Trophy
    },
    {
      title: 'Behavior Change Specialist',
      organization: 'NASM',
      icon: Target
    },
    {
      title: 'CPR/AED',
      organization: 'ASTI',
      icon: Shield
    }
  ]

  const values = [
    {
      icon: Heart,
      title: 'Meet You Where You Are',
      description:
        "I don't ask you to be ready. I ask you to start from where you actually are — your body, your schedule, your life right now."
    },
    {
      icon: Target,
      title: 'Strength With Purpose',
      description:
        'No extremes, no all-or-nothing. Just training that builds real strength you keep, workout after workout.'
    },
    {
      icon: Users,
      title: 'Real Accountability',
      description:
        "You're not doing this alone. I check in, adjust the plan, and tell you the truth — even when it's not what you want to hear."
    },
    {
      icon: Trophy,
      title: 'Progress You Can Keep',
      description:
        "I'm not coaching you toward a 12-week transformation. I'm coaching you toward a life you don't need to escape from again."
    }
  ]

  const approach = [
    'Building strength',
    'Creating a lifestyle',
    'Building consistency',
    "Finding out what's possible for you"
  ]

  return (
    <main className="min-h-screen bg-brand-cream text-brand-navy">
      <Seo
        title="Meet Cassie Tach — Fit by Cassie"
        description="Meet Cassie Tach, IFBB Bikini Pro and certified coach helping women build strength, confidence, and sustainable habits around real life."
        image="/images/cassie-about-hero.webp"
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-brand-cream px-4 sm:px-6 lg:px-8 pt-10 pb-16 sm:pt-14 sm:pb-20 lg:pt-16 lg:pb-24">
        <div
          className="absolute -top-40 -right-32 w-[520px] h-[520px] rounded-full bg-brand-teal/10 blur-3xl pointer-events-none"
          aria-hidden
        />

        <div
          className="absolute -bottom-40 -left-32 w-[420px] h-[420px] rounded-full bg-brand-blush/15 blur-3xl pointer-events-none"
          aria-hidden
        />

        <div className="container-max relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="inline-block text-xs font-bold uppercase tracking-widest-xl text-brand-blush mb-4">
                The Coach Behind Fit by Cassie
              </span>

              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] mb-6 text-brand-navy">
                Meet{' '}
                <em className="block italic font-normal text-brand-teal mt-1">
                  Cassie Tach
                </em>
              </h1>

              <p className="text-xl sm:text-2xl font-display leading-relaxed text-brand-navy/85 mb-6">
                I'm Cassie Tach, founder of Fit by Cassie, an IFBB Bikini Pro,
                and a certified fitness and nutrition coach.
              </p>

              <div className="space-y-4 text-brand-navy/75 leading-relaxed">
                <p>
                  I help women build strength, confidence, and healthier habits
                  with coaching designed around their goals, their starting
                  point, and the lives they're actually living.
                </p>

                <p>
                  I didn't build my fitness in the absence of a busy life. I
                  built it in the middle of one.
                </p>

                <p>
                  That experience shapes how I coach today. Whether you're
                  beginning strength training for the first time, finding your
                  way back to consistency, or working toward a more advanced
                  physique goal, the plan should fit you—not the other way
                  around.
                </p>
              </div>

              <div className="flex flex-wrap gap-2.5 mt-7 mb-8">
                {[
                  'IFBB Bikini Pro',
                  'Certified Personal Trainer',
                  'Certified Nutrition Coach'
                ].map((badge) => (
                  <span
                    key={badge}
                    className="inline-flex items-center rounded-full bg-brand-ivory border border-brand-bronze/25 px-4 py-2 text-sm font-semibold text-brand-navy"
                  >
                    {badge}
                  </span>
                ))}
              </div>

              <blockquote className="border-l-2 border-brand-blush pl-6 py-1 mb-8">
                <p className="font-display text-xl sm:text-2xl italic text-brand-navy leading-relaxed">
                  “Your age is a number. It isn't a set of instructions.”
                </p>
              </blockquote>

              <Link
                to="/book-consultation"
                className="inline-flex items-center justify-center rounded-full bg-brand-navy text-brand-cream font-semibold px-8 py-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-soft-lg"
              >
                Schedule Your Complimentary Consultation
                <ArrowRight className="ml-2" size={18} />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="relative"
            >
              <div className="absolute -inset-4 rounded-[2rem] border border-brand-bronze/20" />

              <div className="relative rounded-3xl overflow-hidden shadow-soft-lg aspect-[5/6] w-full bg-brand-ivory">
                <img
                  src="/images/cassie-about-hero.webp"
                  alt="Cassie Tach, founder and coach of Fit by Cassie"
                  className="w-full h-full object-cover object-[center_18%]"
                />

                <div className="absolute bottom-5 left-5 right-5 sm:right-auto bg-brand-ivory/95 backdrop-blur-sm rounded-2xl shadow-soft px-5 py-4 border border-brand-bronze/20">
                  <p className="font-display text-xl font-semibold text-brand-navy leading-none">
                    Cassie Tach
                  </p>
                  <p className="text-xs text-brand-teal uppercase tracking-widest-xl mt-1.5">
                    Founder · Coach · IFBB Bikini Pro
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* STORY */}
      <section className="bg-brand-ivory px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto"
          >
            <div className="text-center mb-14">
              <span className="inline-block text-xs font-bold uppercase tracking-widest-xl text-brand-teal mb-4">
                My Story
              </span>

              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-brand-navy leading-tight mb-5">
                I didn't always feel{' '}
                <em className="italic font-normal text-brand-teal">strong.</em>
              </h2>

              <p className="text-lg text-brand-navy/70 leading-relaxed">
                I know what it feels like to want something different and not
                know where to begin.
              </p>
            </div>

            <div className="space-y-9 text-brand-navy/75 leading-relaxed text-[1.05rem]">
              <div className="space-y-4">
                <p>
                  I grew up overweight, bullied, and constantly feeling like I
                  didn't belong. I was the girl who stayed in the background —
                  the “nice one” — but never the one who felt chosen, seen, or
                  good enough.
                </p>

                <p>
                  As I got older, I gained more weight and lost more confidence.
                  I avoided mirrors. Clothes didn't fit. I compared myself to
                  everyone. Slowly, I stopped feeling like myself.
                </p>
              </div>

              <div className="space-y-5">
                <p>
                  I tried everything I thought would work. Endless cardio.
                  Guessing with food. Starting and stopping over and over again.
                  I felt stuck, frustrated, and lost.
                </p>

                <blockquote className="relative bg-brand-cream rounded-3xl px-8 py-9 sm:px-10 border border-brand-bronze/20">
                  <span
                    className="font-display text-6xl text-brand-blush/40 absolute top-3 left-6 leading-none"
                    aria-hidden
                  >
                    “
                  </span>

                  <p className="font-display text-xl sm:text-2xl italic text-brand-navy leading-relaxed relative">
                    No one was coming to save me. If I wanted a different life,
                    I had to become a different version of myself.
                  </p>
                </blockquote>
              </div>

              <div className="space-y-4">
                <p>
                  So I started learning. I invested in myself. I trained with
                  purpose. I built discipline. And I kept showing up — even when
                  I didn't feel like it.
                </p>

                <p>
                  Over time, I didn't just change my body. I rebuilt my mindset,
                  my confidence, and my relationship with myself.
                </p>

                <p className="font-display text-xl text-brand-navy">
                  But the biggest win was becoming someone I'm proud of.
                </p>
              </div>

              <div className="space-y-4">
                <p>
                  That experience is part of why I coach the way I do today.
                </p>

                <p>
                  I know what it feels like to question yourself, to wonder
                  where to begin, and to think maybe you've missed your
                  chance.
                </p>

                <p>
                  But I also know that your starting point doesn't determine
                  what's possible next.
                </p>

                <p className="font-display text-xl text-brand-navy">
                  I don't coach an age. I don't coach a number on a scale. I
                  coach the woman in front of me.
                </p>
              </div>

              <div className="space-y-5">
                <p>
                  My approach isn't about extremes or quick fixes. It's about:
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {approach.map((item) => (
                    <div
                      key={item}
                      className="flex items-center gap-3 bg-brand-cream border border-brand-teal/20 rounded-2xl px-5 py-4"
                    >
                      <CheckCircle
                        size={16}
                        className="text-brand-teal shrink-0"
                      />
                      <span className="text-sm font-semibold text-brand-navy">
                        {item}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="text-center pt-8">
                <div className="w-14 h-px bg-brand-bronze mx-auto mb-7" />

                <p className="font-display text-2xl text-brand-navy mb-3">
                  You have one body. One life.
                </p>

                <p className="text-lg text-brand-navy/70 leading-relaxed">
                  And I want you to feel strong enough to keep saying yes to
                  it.
                </p>

                <p className="mt-6">
                  <Link
                    to="/coming-home-to-me"
                    className="inline-flex items-center gap-1.5 text-brand-teal font-semibold hover:text-brand-navy transition-colors"
                  >
                    Read The First Step — a free reflection on starting over
                    <ArrowRight size={16} />
                  </Link>
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CREDENTIALS */}
      <section className="relative overflow-hidden bg-brand-navy px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
        <img
          src="/images/hero-gym-bg.webp"
          alt=""
          aria-hidden
          className="absolute inset-0 w-full h-full object-cover opacity-[0.08]"
        />

        <div className="absolute inset-0 bg-brand-navy/90" />

        <div className="relative container-max">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-14"
          >
            <span className="inline-block text-xs font-bold uppercase tracking-widest-xl text-brand-blush mb-4">
              Credentials & Experience
            </span>

            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-brand-ivory mb-5">
              The knowledge behind the coaching.
            </h2>

            <p className="text-lg text-brand-cream/70 max-w-2xl mx-auto leading-relaxed">
              My experience shapes how I coach. My education helps me put that
              experience to work for you.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-3xl mx-auto">
            {qualifications.map((qual, index) => (
              <motion.div
                key={qual.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.08 }}
                className="flex items-center gap-4 bg-brand-ivory rounded-2xl px-6 py-5 border border-brand-bronze/15"
              >
                <div className="w-11 h-11 rounded-full bg-brand-teal/10 flex items-center justify-center shrink-0">
                  <qual.icon size={20} className="text-brand-teal" />
                </div>

                <div>
                  <h3 className="text-sm font-bold text-brand-navy leading-tight">
                    {qual.title}
                  </h3>

                  <p className="text-xs text-brand-teal mt-1">
                    {qual.organization}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          <p className="mt-10 flex items-center justify-center gap-2 text-sm text-brand-cream/70">
            <Award size={16} className="text-brand-blush" />
            IFBB Bikini Pro · 20+ years of personal fitness experience
          </p>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="bg-brand-cream px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-2 relative"
            >
              <div className="absolute -inset-3 rounded-[2rem] border border-brand-bronze/20" />

              <div className="relative rounded-3xl overflow-hidden shadow-soft-lg aspect-[4/5] bg-brand-ivory">
                <img
                  src="/images/cassie-philosophy.webp"
                  alt="Cassie Tach, Fit by Cassie coach"
                  className="w-full h-full object-cover object-[center_18%]"
                />
              </div>
            </motion.div>

            <div className="lg:col-span-3">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="mb-10"
              >
                <span className="inline-block text-xs font-bold uppercase tracking-widest-xl text-brand-blush mb-4">
                  Coaching Philosophy
                </span>

                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-brand-navy leading-tight mb-5">
                  Build the body.{' '}
                  <em className="italic font-normal text-brand-teal">
                    Break the belief.
                  </em>
                </h2>

                <p className="text-lg text-brand-navy/70 leading-relaxed">
                  Strength is bigger than a workout. The way I coach is designed
                  to help you build trust in yourself, create consistency, and
                  become stronger without requiring your life to be perfect.
                </p>
              </motion.div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {values.map((value, index) => (
                  <motion.div
                    key={value.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.08 }}
                    className="bg-brand-ivory rounded-3xl p-7 shadow-soft border border-brand-bronze/15 transition-all duration-300 hover:-translate-y-1 hover:shadow-soft-lg"
                  >
                    <div className="w-11 h-11 rounded-full bg-brand-blush/15 flex items-center justify-center mb-5">
                      <value.icon size={21} className="text-brand-teal" />
                    </div>

                    <h3 className="text-lg font-bold text-brand-navy mb-2">
                      {value.title}
                    </h3>

                    <p className="text-brand-navy/65 leading-relaxed text-sm">
                      {value.description}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

    </main>
  )
}

export default About
